import React, { useState, useCallback, useRef } from 'react';
import {
  TouchableOpacity,
  TextInput as RNTextInput,
  Image,
  Alert,
  ScrollView,
  Platform,
} from 'react-native';
import type { TextStyle, ViewStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text, useTheme } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';

// Types
import type { RootStackScreenProps } from '@/types/navigation';

// Components
import { SvgBackground } from '@/components';

// API
import { useConfirmPhoneVerification, useRequestPhoneVerification, useLogin } from '@/api/hooks/useAuth';

// Context
import { useAuth } from '@/contexts/AuthContext';

// Icons
import { Smartphone } from 'lucide-react-native';

// Constants
import { COLORS, COMMON_STYLES } from '@/constants';

// Assets
import { logo } from '@/assets';

type Props = RootStackScreenProps<'RegisterStep3'>;

const RegisterStep3Screen: React.FC<Props> = ({ route }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { checkAuth } = useAuth();

  // Route params
  const userId = route.params?.userId ?? '';
  const email = route.params?.email ?? '';
  const password = route.params?.password ?? '';
  const phone = route.params?.phone ?? '';
  const dialCode = route.params?.dialCode ?? '+48';

  // State
  const [smsCode, setSmsCode] = useState(['', '', '', '']);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);

  // Refs
  const inputRefs = useRef<RNTextInput[]>([]);

  // API Hooks - Login after phone verification
  const { mutate: login, isPending: isLoggingIn } = useLogin({
    onSuccess: async () => {
      // Token is set automatically by useLogin hook via apiClient.setAuthToken
      // Refresh AuthContext to recognize the new token
      await checkAuth();
      // Navigation will happen automatically when isAuthenticated becomes true
    },
    onError: (error) => {
      const errorMessage = error.message || t('errors.loginFailed', 'Nie udało się zalogować');
      setApiError(errorMessage);
      Alert.alert(
        t('errors.error', 'Błąd'),
        errorMessage
      );
    },
  });

  const { mutate: confirmVerification, isPending: isSubmitting } = useConfirmPhoneVerification({
    onSuccess: () => {
      setApiError(null);
      // After successful phone verification, log in the user
      login({
        username: email,
        password: password,
      });
    },
    onError: (error) => {
      const errorMessage = error.message || t('errors.invalidCode', 'Nieprawidłowy kod weryfikacyjny');
      setApiError(errorMessage);
      Alert.alert(
        t('errors.error', 'Błąd'),
        errorMessage
      );
    },
  });

  const { mutate: resendVerification } = useRequestPhoneVerification({
    onSuccess: () => {
      setIsResending(false);
      setApiError(null);
      Alert.alert(
        t('screens.sms.codeSent', 'Kod wysłany'),
        t('screens.sms.codeSentMessage', 'Nowy kod SMS został wysłany')
      );
    },
    onError: (error) => {
      setIsResending(false);
      const errorMessage = error.message || t('errors.resendFailed', 'Nie udało się wysłać kodu ponownie');
      setApiError(errorMessage);
      Alert.alert(
        t('errors.error', 'Błąd'),
        errorMessage
      );
    },
  });

  // Handlers
  const handleCodeChange = useCallback(
    (text: string, index: number) => {
      const newCode = [...smsCode];
      newCode[index] = text;
      setSmsCode(newCode);

      // Auto-focus next input
      if (text && index < 3) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [smsCode],
  );

  const handleKeyPress = useCallback(
    (e: any, index: number) => {
      // Go back to previous input on backspace
      if (e.nativeEvent.key === 'Backspace' && !smsCode[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [smsCode],
  );

  // Computed values - moved before handlers that use them
  const isProcessing = isSubmitting || isLoggingIn;

  const handleVerify = useCallback(() => {
    const code = smsCode.join('');
    if (isProcessing || code.length !== 4 || !userId) return;

    setApiError(null);
    confirmVerification({
      userId,
      code,
    });
  }, [isProcessing, smsCode, userId, confirmVerification]);

  const handleResendCode = useCallback(() => {
    if (isResending || !userId) return;

    setIsResending(true);
    // Reset code inputs
    setSmsCode(['', '', '', '']);
    inputRefs.current[0]?.focus();
    
    // Resend verification code
    const fullPhoneNumber = `${dialCode}${phone}`;
    resendVerification({
      userId,
      phoneNumber: fullPhoneNumber,
    });
  }, [isResending, userId, dialCode, phone, resendVerification]);

  // Computed values
  const isCodeComplete = smsCode.every(digit => digit.length === 1);
  const formattedPhone = `${dialCode} ${phone}`;
  const isWeb = Platform.OS === 'web';

  // Web layout - matching AuthScreen/RegisterScreen design
  if (isWeb) {
    return (
      <ScrollView 
        style={{ flex: 1, backgroundColor: theme.background?.val }} 
        contentContainerStyle={{ flexGrow: 1, paddingHorizontal: 20, paddingVertical: 20, justifyContent: 'space-between' }}
        showsVerticalScrollIndicator={false}
      >
        {/* Main content */}
        <YStack>
          {/* Title */}
          <YStack alignItems="center" marginBottom="$4">
            <Text fontSize={26} fontWeight="500" color="$color" textAlign="center">
              {t('screens.sms.title', 'Kod SMS')}
            </Text>
            <Text fontSize={13} color="$color10" textAlign="center" marginTop={8} paddingHorizontal="$2">
              {t('screens.sms.subtitle', 'Wprowadź 4-cyfrowy kod')}
            </Text>
          </YStack>

          {/* Icon */}
          <YStack alignItems="center" marginBottom="$4">
            <Smartphone size={48} color={theme.color10?.val} />
          </YStack>

          <Text
            color="$color10"
            fontSize={13}
            textAlign="center"
            marginBottom="$2"
          >
            {t('screens.sms.instruction', 'Wysłaliśmy kod SMS na numer')}
          </Text>

          <Text color="$color" fontSize={15} fontWeight="600" textAlign="center" marginBottom="$4">
            {formattedPhone}
          </Text>

          {/* SMS Code Input */}
          <XStack justifyContent="center" alignItems="center" space="$3" marginBottom="$4" width="100%">
            {smsCode.map((digit, index) => (
              <RNTextInput
                key={index}
                ref={ref => {
                  if (ref) inputRefs.current[index] = ref;
                }}
                value={digit}
                onChangeText={text => handleCodeChange(text.slice(-1), index)}
                onKeyPress={e => handleKeyPress(e, index)}
                keyboardType="numeric"
                maxLength={1}
                style={getWebCodeInputStyle(theme, digit.length > 0)}
                textAlign="center"
              />
            ))}
          </XStack>

          {/* API Error Display */}
          {apiError && (
            <Text color={COLORS.ERROR} fontSize={11} textAlign="center" marginBottom="$2">
              {apiError}
            </Text>
          )}

          <TouchableOpacity
            activeOpacity={0.9}
            disabled={!isCodeComplete || isProcessing}
            onPress={handleVerify}
            style={getWebButtonStyle(!isCodeComplete || isProcessing)}
          >
            <Text color="white" fontSize={14} fontWeight="600">
              {isLoggingIn 
                ? t('screens.sms.loggingIn', 'Logowanie...') 
                : t('screens.sms.verify', 'Zweryfikuj')}
            </Text>
          </TouchableOpacity>

          <YStack alignItems="center" marginTop={16}>
            <Text color="$color10" fontSize={11} textAlign="center">
              {t('screens.sms.noCode', 'Nie otrzymałeś kodu?')}
            </Text>
            <TouchableOpacity onPress={handleResendCode}>
              <Text color={COLORS.PRIMARY} fontSize={11} fontWeight="500" marginTop={8}>
                {t('screens.sms.resend', 'Wyślij ponownie')}
              </Text>
            </TouchableOpacity>
          </YStack>
        </YStack>

        {/* Progress Stepper - at bottom */}
        <YStack alignItems="center">
          <XStack space="$2" alignItems="center">
            <YStack width={8} height={8} borderRadius={4} backgroundColor={COLORS.PRIMARY} />
            <YStack width={8} height={8} borderRadius={4} backgroundColor={COLORS.PRIMARY} />
            <YStack width={8} height={8} borderRadius={4} backgroundColor={COLORS.PRIMARY} />
          </XStack>
          <Text color="$color10" fontSize={11} marginTop={8}>
            {t('screens.sms.step3', 'Krok 3 z 3')}
          </Text>
        </YStack>
      </ScrollView>
    );
  }

  // Mobile layout - original design
  return (
    <YStack flex={1} backgroundColor="$background">
      {/* Header */}
      <XStack
        paddingTop="$8"
        paddingHorizontal="$4"
        paddingBottom="$3"
        justifyContent="space-between"
        alignItems="center"
        position="relative"
        zIndex={10}
      >
        <XStack space="$3" />
      </XStack>

      {/* Hero Section */}
      <YStack>
        <SvgBackground height={220}>
          <Image
            source={logo}
            style={COMMON_STYLES.logo}
          />

          <Text
            fontSize="$8"
            fontWeight="bold"
            color="white"
            textAlign="center"
            fontFamily="Poppins"
          >
            {t('screens.sms.title', 'Kod SMS')}
          </Text>

          <Text
            fontSize="$6"
            color="white"
            opacity={0.9}
            textAlign="center"
            fontFamily="Poppins"
            maxWidth="80%"
          >
            {t('screens.sms.subtitle', 'Wprowadź 4-cyfrowy kod')}
          </Text>
        </SvgBackground>
      </YStack>

      {/* Form */}
      <YStack
        paddingHorizontal="$4"
        paddingTop="$4"
        space="$4"
        zIndex={2}
        alignItems="center"
      >
        <YStack paddingTop="$4">
          <Smartphone size={60} color={theme.color10?.val} />
        </YStack>

        <Text
          color="$color10"
          fontSize="$4"
          textAlign="center"
          maxWidth="90%"
          lineHeight={24}
        >
          {t('screens.sms.instruction', 'Wysłaliśmy kod SMS na numer')}
        </Text>

        <Text color="$color" fontSize="$5" fontWeight="600" textAlign="center">
          {formattedPhone}
        </Text>

        {/* SMS Code Input */}
        <XStack space="$3" paddingVertical="$4">
          {smsCode.map((digit, index) => (
            <RNTextInput
              key={index}
              ref={ref => {
                if (ref) inputRefs.current[index] = ref;
              }}
              value={digit}
              onChangeText={text => handleCodeChange(text.slice(-1), index)}
              onKeyPress={e => handleKeyPress(e, index)}
              keyboardType="numeric"
              maxLength={1}
              style={getCodeInputStyle(theme, digit.length > 0)}
              textAlign="center"
            />
          ))}
        </XStack>

        {/* API Error Display */}
        {apiError && (
          <Text color={COLORS.ERROR} fontSize="$3" textAlign="center">
            {apiError}
          </Text>
        )}

        <TouchableOpacity
          activeOpacity={0.9}
          disabled={!isCodeComplete || isProcessing}
          onPress={handleVerify}
          style={getVerifyButtonStyle(theme, !isCodeComplete || isProcessing)}
        >
          <Text color="white" fontSize="$4" fontWeight="600">
            {isLoggingIn 
              ? t('screens.sms.loggingIn', 'Logowanie...') 
              : t('screens.sms.verify', 'Zweryfikuj')}
          </Text>
        </TouchableOpacity>

        <YStack alignItems="center" space="$3" paddingTop="$4">
          <Text color="$color10" fontSize="$3" textAlign="center">
            {t('screens.sms.noCode', 'Nie otrzymałeś kodu?')}
          </Text>

          <TouchableOpacity onPress={handleResendCode}>
            <Text color={COLORS.PRIMARY} fontSize="$3" fontWeight="500">
              {t('screens.sms.resend', 'Wyślij ponownie')}
            </Text>
          </TouchableOpacity>
        </YStack>

        {/* Progress Indicator */}
        <YStack
          alignItems="center"
          space="$2"
          paddingTop="$6"
          paddingBottom="$6"
        >
          <Text color="$color10" fontSize="$3">
            {t('screens.sms.step3', 'Krok 3 z 3')}
          </Text>
          <XStack space="$2">
            <YStack
              width={8}
              height={8}
              borderRadius={4}
              backgroundColor="$color8"
            />
            <YStack
              width={8}
              height={8}
              borderRadius={4}
              backgroundColor="$color8"
            />
            <YStack
              width={8}
              height={8}
              borderRadius={4}
              backgroundColor="$color10"
            />
          </XStack>
        </YStack>
      </YStack>
    </YStack>
  );
};

// Web styles
function getWebCodeInputStyle(
  theme: ReturnType<typeof useTheme>,
  hasValue: boolean,
): TextStyle {
  return {
    width: 50,
    height: 50,
    borderWidth: 1,
    borderRadius: 10,
    borderColor: hasValue
      ? COLORS.PRIMARY
      : theme.borderColor?.val ?? '#e5e7eb',
    backgroundColor: theme.background?.val,
    color: theme.color?.val,
    fontSize: 20,
    fontWeight: '600',
    fontFamily: 'Poppins',
    textAlign: 'center',
    textAlignVertical: 'center',
  };
}

function getWebButtonStyle(disabled: boolean): ViewStyle {
  return {
    height: 46,
    borderRadius: 10,
    backgroundColor: disabled ? COLORS.DISABLED : COLORS.PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
  };
}

// Mobile styles
function getCodeInputStyle(
  theme: ReturnType<typeof useTheme>,
  hasValue: boolean,
): TextStyle {
  return {
    width: 60,
    height: 60,
    borderWidth: 2,
    borderRadius: 12,
    borderColor: hasValue
      ? COLORS.PRIMARY
      : theme.borderColor?.val ?? '#e5e7eb',
    backgroundColor: theme.background?.val,
    color: theme.color?.val,
    fontSize: 24,
    fontWeight: '600',
  };
}

function getVerifyButtonStyle(
  theme: ReturnType<typeof useTheme>,
  disabled: boolean,
): ViewStyle {
  return {
    ...COMMON_STYLES.button,
    backgroundColor: disabled ? COLORS.DISABLED : COLORS.PRIMARY,
    width: '100%',
  };
}

export default RegisterStep3Screen;
